/**
 * Bank-connection query hooks (Phase 2, §7.5).
 *
 * `useConnections` lists every household connection; `useConnection` loads one
 * with its `recent_runs`. Both are short-lived caches — a sync or re-auth
 * invalidates `['connections']`, so the list reflects fresh status/countdowns.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type { ConnectionDetailOut, ConnectionOut } from '../types.ts';

const STALE = 30 * 1000;

/** All bank connections (household-visible). */
export function useConnections(): UseQueryResult<ConnectionOut[]> {
  return useQuery({
    queryKey: qk.connections,
    queryFn: () => api.get<ConnectionOut[]>('/api/connections'),
    staleTime: STALE,
  });
}

/** One connection incl. `recent_runs`; disabled when `id` is not a number. */
export function useConnection(id: number): UseQueryResult<ConnectionDetailOut> {
  return useQuery({
    queryKey: qk.connection(id),
    queryFn: () => api.get<ConnectionDetailOut>(`/api/connections/${id}`),
    staleTime: STALE,
    enabled: Number.isFinite(id),
  });
}
